import React from 'react';
import { Compass, MonitorSmartphone, Newspaper, ShieldCheck, MessagesSquare, LucideIcon } from 'lucide-react';
import { Theme, ThemeId, THEMES, themeById, loadProgress, themeProgressPct, fmt, GOLD, ROYAL } from '../../data/learning';

export const THEME_ICON: Record<ThemeId, LucideIcon> = {
  ai: Compass,            // 'Wegwijs in AI' — bewust geen sparkles-cliché
  dg: MonitorSmartphone,
  mw: Newspaper,
  dv: ShieldCheck,
  sm: MessagesSquare,     // social media — gesprek/verbinding
};

// Voortgangsbalk met instelbare kleur.
export const Track: React.FC<{ pct: number; color?: string; height?: number }> = ({ pct, color = ROYAL, height = 8 }) => (
  <span className="block w-full rounded-full bg-line overflow-hidden" style={{ height }}>
    <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color, transition: 'width .3s ease' }} />
  </span>
);

// Solide, gekleurd thema-merkteken (bold en branded, geen zacht generiek vlakje).
export const ThemeGlyph: React.FC<{ theme: Theme; size?: number }> = ({ theme, size = 52 }) => {
  const Icon = THEME_ICON[theme.id];
  return (
    <span className="grid place-items-center rounded-tile flex-none" style={{ width: size, height: size, background: theme.accent }}>
      <Icon color="#fff" strokeWidth={1.9} width={size * 0.48} height={size * 0.48} />
    </span>
  );
};

// Zelfscan-vs-toets-vergelijking (goud = inschatting, blauw = resultaat).
export const ComparePanel: React.FC<{ title?: string }> = ({ title = 'Je inschatting vs. je toets' }) => {
  const store = loadProgress();
  return (
    <div className="card p-5 sm:p-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-display text-lg font-bold text-ink">{title}</h2>
        <div className="flex items-center gap-4 text-xs text-sub">
          <span className="inline-flex items-center gap-1.5"><i className="inline-block w-3 h-3 rounded-sm" style={{ background: GOLD }} /> Zelfscan</span>
          <span className="inline-flex items-center gap-1.5"><i className="inline-block w-3 h-3 rounded-sm" style={{ background: ROYAL }} /> Toets</span>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {THEMES.map((t) => {
          const st = store[t.id];
          return (
            <div key={t.id} className="grid items-center gap-3" style={{ gridTemplateColumns: 'minmax(120px,180px) 1fr' }}>
              <span className="text-sm font-medium text-ink truncate">{t.name}</span>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1"><Track pct={st.sa != null ? st.sa * 10 : 0} color={GOLD} height={7} /></div>
                  <span className="text-xs tabular-nums w-16 text-right" style={{ color: st.sa != null ? '#17130E' : '#9A938A' }}>{st.sa != null ? fmt(st.sa) : '—'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1"><Track pct={st.toets != null ? st.toets * 10 : 0} color={ROYAL} height={7} /></div>
                  <span className="text-xs tabular-nums w-16 text-right" style={{ color: st.toets != null ? '#17130E' : '#9A938A' }}>{st.toets != null ? fmt(st.toets) : 'nog open'}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { themeById, themeProgressPct };
